import React from 'react';
import { Popconfirm } from 'antd';
import {
    StopOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    UndoOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import styles from './styles/reports-table.module.scss';

/**
 * Up to three icon-buttons. The two sync ones wrap an antd Popconfirm, which
 * stays on antd because the design system ships no inline-confirm popover.
 *
 * Restrict has no Popconfirm: it now goes through the page-level
 * UserActionConfirmModal, so the row, the detail panel and /users/user confirm
 * the same way with the same copy. The two sync actions keep theirs — an inline
 * popover is the right weight for them, and they are a different capability.
 *
 * One permission flag per button, because the three are three different
 * capabilities server-side: `admin:users:restrict` toggles a member's platform
 * access, `admin:sync:delete_latest` destroys their latest sync row, and
 * `admin:sync:mark_complete` only nudges that row's status forward. A role can
 * hold any subset, so the toolbar renders exactly what the role can use and
 * disappears only when it can use none.
 *
 * @param {Object} props
 * @param {string} props.clientId
 * @param {boolean} props.isRestricted
 * @param {boolean} props.restrictBusy
 * @param {boolean} props.deleteBusy
 * @param {boolean} props.markBusy
 * @param {(clientId: string, isRestricted: boolean) => void} [props.onRestrictUser]
 * @param {(clientId: string) => void} [props.onDeleteLatestSync]
 * @param {(clientId: string) => void} [props.onMarkLatestSyncComplete]
 * @param {boolean} [props.canRestrict] Gates the restrict / restore action.
 * @param {boolean} [props.canDeleteSync] Gates deleting the latest sync row.
 * @param {boolean} [props.canMarkSyncComplete] Gates marking it complete.
 */
function UserActionsCell({
    clientId,
    isRestricted,
    restrictBusy,
    deleteBusy,
    markBusy,
    onRestrictUser,
    onDeleteLatestSync,
    onMarkLatestSyncComplete,
    canRestrict = true,
    canDeleteSync = true,
    canMarkSyncComplete = true,
}) {
    const restrictIcon = restrictBusy
        ? <LoadingOutlined />
        : (isRestricted ? <UndoOutlined /> : <StopOutlined />);
    const restrictLabel = isRestricted ? 'Restore access' : 'Restrict access';

    if (!canRestrict && !canDeleteSync && !canMarkSyncComplete) {
        return null;
    }

    return (
        <div
            className={styles.actionToolbar}
            onClick={(e) => e.stopPropagation()}>
            {canRestrict ? (
                <button
                    type="button"
                    className={[
                        styles.actionBtn,
                        isRestricted ? styles.actionBtnInfo : styles.actionBtnDanger,
                        restrictBusy ? styles.actionBtnBusy : '',
                    ].join(' ')}
                    disabled={restrictBusy}
                    onClick={() => onRestrictUser && onRestrictUser(clientId, isRestricted)}
                    aria-label={restrictLabel}>
                    {restrictIcon}
                </button>
            ) : null}

            {canDeleteSync ? (
                <Popconfirm
                    title="Delete the latest sync row?"
                    description="The most recent sync record will be removed permanently. The next sync will start fresh."
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                    placement="topRight"
                    onConfirm={() => onDeleteLatestSync && onDeleteLatestSync(clientId)}
                >
                    <button
                        type="button"
                        className={[
                            styles.actionBtn,
                            styles.actionBtnDanger,
                            deleteBusy ? styles.actionBtnBusy : '',
                        ].join(' ')}
                        disabled={deleteBusy}
                        aria-label="Delete latest sync row">
                        {deleteBusy ? <LoadingOutlined /> : <DeleteOutlined />}
                    </button>
                </Popconfirm>
            ) : null}

            {canMarkSyncComplete ? (
                <Popconfirm
                    title="Mark latest sync complete?"
                    description="Forces the most recent sync row's status to COMPLETE. Per-dimension states are unchanged."
                    okText="Mark complete"
                    cancelText="Cancel"
                    placement="topRight"
                    onConfirm={() => onMarkLatestSyncComplete && onMarkLatestSyncComplete(clientId)}
                >
                    <button
                        type="button"
                        className={[
                            styles.actionBtn,
                            styles.actionBtnSuccess,
                            markBusy ? styles.actionBtnBusy : '',
                        ].join(' ')}
                        disabled={markBusy}
                        aria-label="Mark latest sync complete">
                        {markBusy ? <LoadingOutlined /> : <CheckCircleOutlined />}
                    </button>
                </Popconfirm>
            ) : null}
        </div>
    );
}

export default UserActionsCell;
